import { useCallback, useEffect, useRef, useState } from 'react';
import { idleWatcher as defaultWatcher } from '../utils/idleWatcher';
import { dismiss as persistDismissId, isDismissed } from '../utils/tooltipDismissStore';

/**
 * Open/close state for Tooltip: idle watcher + session dismiss + controlled hatch.
 * Hover/manual triggers skip the watcher; callers drive setOpen themselves.
 */
export function useIdleReveal({
  id,
  trigger = 'idle',
  idleDelay = 7000,
  persistDismiss = 'session',
  priority = 0,
  open: openProp,
  onOpenChange,
  onDismiss,
  watcher = defaultWatcher,
} = {}) {
  const [dismissed, setDismissed] = useState(() => (
    persistDismiss === 'session' && id ? isDismissed(id) : false
  ));
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;

  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  const setOpen = useCallback((next) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChangeRef.current?.(next);
  }, [isControlled]);

  useEffect(() => {
    if (trigger !== 'idle' || dismissed) return undefined;
    return watcher.subscribe({
      delay: idleDelay,
      priority,
      onIdle: () => setOpen(true),
      onActive: () => setOpen(false),
    });
  }, [trigger, idleDelay, priority, dismissed, watcher, setOpen]);

  const dismiss = useCallback(() => {
    if (id && persistDismiss === 'session') {
      persistDismissId(id);
      setDismissed(true);
    }
    setOpen(false);
    onDismissRef.current?.();
  }, [id, persistDismiss, setOpen]);

  return { open: dismissed ? false : open, setOpen, dismiss, dismissed };
}
